/**
 * PublicMailItemView — citizen-facing projection of a MailItem.
 *
 * !! KEEP IN SYNC WITH:
 *    apps/service-mail/src/modules/mail-item/mail-item.dto.ts (PublicMailItemView)
 *    apps/service-mail/src/modules/mail-item/mail-item.service.ts (getPublicTracking)
 *
 * The two apps don't share a workspace lib for this contract yet, so this
 * file duplicates the shape on purpose. Any drift is a contract break —
 * the citizen page will fall through to the "malformed" error state.
 *
 * Anti-PII contract (F-035):
 *   - NEVER add sender / receiver / notes / recordedBy / pricing fields.
 *   - locationCode is a hub or outlet code (geographic), not personal.
 */

export interface PublicTrackingEventView {
  eventType: string;
  recordedAt: string;
  /** Outlet / hub code, or `null` if the event was recorded without one. */
  locationCode: string | null;
}

export interface PublicMailItemView {
  trackingNumber: string;
  status: string;
  serviceClassDisplay: string;
  registeredAt: string;
  updatedAt: string;
  /** ISO timestamp; service-mail today returns registeredAt + 5 business days. */
  estimatedDelivery: string;
  events: PublicTrackingEventView[];
}

/** Runtime guard. The body is whatever JSON the server returned — we don't
 *  trust it implicitly so we structurally validate before surfacing it. */
export function isPublicMailItemView(value: unknown): value is PublicMailItemView {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v['trackingNumber'] !== 'string') return false;
  if (typeof v['status'] !== 'string') return false;
  if (typeof v['serviceClassDisplay'] !== 'string') return false;
  if (typeof v['registeredAt'] !== 'string') return false;
  if (typeof v['updatedAt'] !== 'string') return false;
  if (typeof v['estimatedDelivery'] !== 'string') return false;
  if (!Array.isArray(v['events'])) return false;
  for (const e of v['events']) {
    if (!isPublicTrackingEventView(e)) return false;
  }
  return true;
}

function isPublicTrackingEventView(value: unknown): value is PublicTrackingEventView {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v['eventType'] !== 'string') return false;
  if (typeof v['recordedAt'] !== 'string') return false;
  const loc = v['locationCode'];
  if (loc !== null && typeof loc !== 'string') return false;
  return true;
}
